import { Analytics } from '@trello/atlassian-analytics';
import { forNamespace, forTemplate } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { PrivateIcon } from '@trello/nachos/icons/private';
import { PublicIcon } from '@trello/nachos/icons/public';
import { UnlockedIcon } from '@trello/nachos/icons/unlocked';
import { IconGreenColor, IconRedColor } from '@trello/nachos/icon';
import { token } from '@trello/theme';
import { Popover, usePopover } from '@trello/nachos/popover';
import { WorkspaceSettingsVisibilityPopoverContent } from './WorkspaceSettingsVisibilityPopoverContent';
import { useWorkspaceSettingsVisibilityQuery } from './WorkspaceSettingsVisibilityQuery.generated';
import type { FunctionComponent } from 'react';
import React, { useCallback, useMemo } from 'react';
import { WorkspaceSettingsSection } from './WorkspaceSettingsSection';
import styles from './WorkspaceSettingsVisibility.less';
import { hasEnforcedSetting } from '@trello/enterprise';
import { memberId } from '@trello/session-cookie';

const format = forTemplate('org_account', { shouldEscapeStrings: false });
const formatOrgPerms = forNamespace('org perms', {
  shouldEscapeStrings: false,
});
const formatViewTitle = forNamespace('view title');

interface WorkspaceSettingsVisibilityProps {
  orgId: string;
}

export const WorkspaceSettingsVisibility: FunctionComponent<WorkspaceSettingsVisibilityProps> = ({
  orgId,
}) => {
  const {
    triggerRef,
    toggle,
    popoverProps,
    hide,
  } = usePopover<HTMLButtonElement>();
  const visibilityIcons = useMemo(
    () => ({
      private: (
        <PrivateIcon
          color={token('color.icon.accent.red', IconRedColor)}
          size="small"
        />
      ),
      domain: <UnlockedIcon size="small" />,
      public: (
        <PublicIcon
          color={token('color.icon.accent.green', IconGreenColor)}
          size="small"
        />
      ),
    }),
    [],
  );

  const { data, loading, error } = useWorkspaceSettingsVisibilityQuery({
    variables: { orgId },
    waitOn: ['MemberHeader'],
  });

  const currentVisibility =
    data?.organization?.prefs?.permissionLevel ?? undefined;
  const VisibilityIcon =
    (currentVisibility && visibilityIcons[currentVisibility]) ?? null;
  const isEnterpriseAdmin = memberId
    ? data?.organization?.enterprise?.idAdmins?.includes(memberId)
    : false;

  const isAdmin =
    (isEnterpriseAdmin ||
      data?.organization?.memberships[0].memberType === 'admin') ??
    false;
  const isEnterpriseRestrictionEnforced = hasEnforcedSetting(
    'permissionLevel',
    data?.organization?.enterprise?.organizationPrefs ?? {},
  );
  const isEditable = isAdmin && !isEnterpriseRestrictionEnforced;
  const enterpriseId = data?.organization?.enterprise?.id;
  const enterpriseName = data?.organization?.enterprise?.displayName;
  const displayName = data?.organization?.domain?.displayName;

  const onClickChange = useCallback(() => {
    toggle();

    Analytics.sendClickedButtonEvent({
      buttonName: 'changeWorkspaceVisibilityButton',
      source: 'workspaceSettingsScreen',
      containers: {
        workspace: {
          id: orgId,
        },
        enterprise: {
          id: enterpriseId,
        },
      },
    });
  }, [orgId, enterpriseId, toggle]);

  if (loading || error) {
    return null;
  }

  return (
    <>
      <WorkspaceSettingsSection
        title={format('team-visibility')}
        detail={
          <p>
            {VisibilityIcon}
            <span
              // Reusing old strings that have HTML strings inside the translation
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: format(`${currentVisibility}-helpertext-1`, {
                  helperText: formatOrgPerms(`${currentVisibility}.text`, {
                    enterpriseName: displayName as string,
                  }),
                }),
              }}
              className={styles.detail}
            />
          </p>
        }
        action={
          <Button
            ref={triggerRef}
            onClick={onClickChange}
            isDisabled={!isEditable}
          >
            {format('change')}
          </Button>
        }
        isEditable={isEditable}
        restrictionMessage={
          isEnterpriseRestrictionEnforced
            ? format('setting-completely-inherited', {
                enterpriseDisplayName: enterpriseName,
              })
            : undefined
        }
      />
      <Popover
        title={formatViewTitle('select team visibility')}
        noHorizontalPadding
        {...popoverProps}
      >
        <WorkspaceSettingsVisibilityPopoverContent
          orgId={orgId}
          currentVisibility={currentVisibility}
          onChange={hide}
        />
      </Popover>
    </>
  );
};
